import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    useCopilotChat,
    useCopilotContext,
    useCoAgent,
    useCopilotAction,
} from "@copilotkit/react-core";
import { Role, TextMessage } from "@copilotkit/runtime-client-gql";
import ModalContainer from "components/common/ModalContainer";
import { truncate } from "lodash";
import Button from "components/common/Button";
import SalesTargetTable from "./SalesTargetTable";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { chatActions } from "redux/Ldb/actions";
import useGetAllAiConversations from "hooks/Chat/useGetAllAiConversations";
import moment from "moment";
import ReactMarkdown from "react-markdown";
import { AgentState, MediaAttachments } from "types/salesTargetType";
import SalesTargetThinkingState from "./SalesTargetThinkingState";
import Dropdown from "components/common/Dropdown";
import CustomToast from "components/common/CustomToast";
import { isFileSizeLessThanOrEqualTo } from "helpers/compareFileSizeInMb";
import toast from "react-hot-toast";
import { displayFileIcon, getFromStorage } from "helpers";
import { getFileTypeFromUrl } from "helpers/getFileTypeFromUrl";
import { firebaseService } from "services/firebaseService";
import Loader from "components/common/Loader";
import CustomEditor from "./Editor";
import SynthTooltip from "components/common/SynthTooltip";
import { useLocation } from "react-router-dom";
import { useMergedSearchParams } from "helpers/mergeSearchParams";

const SalesTargetAgent = ({
    open,
    setOpen,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
}) => {
    const location = useLocation();
    const userProfile = getFromStorage("ally-user");
    const dispatch = useAppDispatch();
    const scrollContainer = document.getElementById("scrollContainer");
    const chatRef = useRef<HTMLDivElement>(null);
    const renameInputRef = useRef<HTMLInputElement>(null);
    const [message, setMessage] = useState<string>("");
    const [threadId, setThreadId] = useState<string>("");
    const [previousChatLayout, setPreviousChatLayout] = useState<boolean>(false);
    const [previousChat, setPreviousChat] = useState<{ [key: string]: any }>({});
    const [loadingAttachName, setLoadingAttachName] = useState<string>("");
    const [pinAction, setPinAction] = useState<string>("");
    const [confirmDeleteChat, setConfirmDeleteChat] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<string>("");
    const [nextThreadId, setNextThreadId] = useState<string>("");
    const [filesAttached, setFilesAttached] = useState<{ [key: string]: any }[]>([]);
    const [selectedIds, setSelectedIds] = useState<{ [key: string]: any }[]>([]);
    const [currentSelectedIds, setCurrentSelectedIds] = useState<{ [key: string]: any }[]>([]);
    const [renameId, setRenameId] = useState<string>("");
    const [openInputId, setOpenInputId] = useState<string>("");
    const [newName, setNewName] = useState<string>("");
    const [oldName, setOldName] = useState<string>("");
    const [currentChat, setCurrentChat] = useState<string>("");
    const [currentThreadId, setCurrentThreadId] = useState<string>("");
    const [uploadingDoc, setUploadingDoc] = useState<boolean>(false);
    const { updateSearchParams } = useMergedSearchParams();
    const { selectedProducts } = location.state || [];
    const {
        createConversationSuccess,
        updateConversationSuccess,
        updatingConversation,
        deleteConversationSuccess,
        deletingConversation,
        fetchChatThreadSuccess,
    } = useAppSelector((state) => state.chat);

    const {
        data: allConversations,
        isLoadingMore: loadingMoreConverstions,
        updatedPage,
    } = useGetAllAiConversations(scrollContainer);

    const {
        visibleMessages,
        appendMessage,
        setMessages,
        deleteMessage,
        reloadMessages,
        stopGeneration,
        reset,
        isLoading,
    } = useCopilotChat();
    // @ts-ignore
    useCopilotAction({
        name: "GenerateSalesTargetForecast",
        description: "Generate the sales target forecast for a list of inventory items.",
        available: "disabled",
        render: ({ args, status, result }) => {
            if (status !== "complete") {
                return <div>Loading...</div>;
            }

            return <></>;
        },
    });

    const { state, setState, nodeName, running } = useCoAgent<AgentState>({
        name: "synth-gpt",
        initialState: {},
        config: {
            subgraphs: true,
            configurable: {
                ldbId: userProfile?.user?._id,
                thread_id: threadId || "",
            },
        },
    });

    const handleUpload = (fileName, file) => {
        setUploadingDoc(true);
        firebaseService.uploadFile(
            `synthally/${process.env.REACT_APP_STAGE}/purchase-order-docs/${fileName}`,
            file,
            (url) => {
                setFilesAttached((prevFiles) => [
                    ...prevFiles,
                    {
                        fileName: fileName,
                        path: url,
                    },
                ]);
                setUploadingDoc(false);
                setLoadingAttachName("");
            }
        );
    };

    const deleteAttachment = (idx) => {
        const copyData = [...filesAttached];
        copyData?.splice(idx, 1);
        setFilesAttached(copyData);
    };

    useEffect(() => {
        setState((prev) => ({
            ...prev,
            media: filesAttached?.length > 0 ? (filesAttached as MediaAttachments[]) : [],
            productContext:
                selectedIds?.length > 0
                    ? selectedIds?.map((id) => ({
                          productName: id?.productName,
                          sku: id?.sku,
                          inventoryId: id?._id,
                      }))
                    : [],
        }));
    }, [filesAttached, selectedIds]);

    useEffect(() => {
        setFilesAttached([]);
        setSelectedIds([]);
        setMessage("");
    }, [threadId]);

    //grouped conversations
    const groupedConversations = (allConversations: { [key: string]: any }[] | null) => {
        const pinned = allConversations.filter((c) => c.isPinned);
        const unpinned = allConversations.filter((c) => !c.isPinned);

        const sorted = [...pinned, ...unpinned];
        const grouped = sorted?.reduce(
            (acc: Record<string, typeof allConversations>, conversation) => {
                const days = moment().diff(moment(conversation?.updatedAt), "days");
                const month = moment(conversation?.updatedAt).format("MMMM YYYY");
                const isPinned = conversation?.isPinned;
                if (isPinned) {
                    acc["Pinned Chats"] = acc["Pinned Chats"] || [];
                    acc["Pinned Chats"].push(conversation);
                } else if (days <= 7) {
                    acc["Last 7 days"] = acc["Last 7 days"] || [];
                    acc["Last 7 days"].push(conversation);
                } else if (days <= 30) {
                    acc["Last 30 days"] = acc["Last 30 days"] || [];
                    acc["Last 30 days"].push(conversation);
                } else if (days > 30) {
                    acc[month] = acc[month] || [];
                    acc[month].push(conversation);
                }

                return acc;
            },
            {}
        );

        return grouped;
    };
    const getData = useMemo(() => {
        if (allConversations?.length > 0) {
            return groupedConversations(allConversations as { [key: string]: any }[]);
        }
    }, [allConversations]);

    useEffect(() => {
        setPreviousChat(getData);
        setNewName("");
        setOpenInputId("");
    }, [getData, allConversations]);

    const handleCreateConversation = () => {
        //disable start for new chat if there is a chat open
        if (visibleMessages?.length > 0) {
            const body = {
                title: "New Chat",
                receiverType: "synth-gpt",
                conversationType: "sales_target_forecast",
            };
            dispatch(chatActions.createConversation(body));
        }
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                renameId &&
                renameInputRef?.current &&
                !renameInputRef?.current?.contains(e.target as Node)
            ) {
                if (newName !== oldName && newName !== "") {
                    dispatch(
                        chatActions.updateConversation({
                            title: newName || "New chat",
                            conversationId: renameId,
                        })
                    );
                    setRenameId("");
                } else {
                    setRenameId("");
                    setNewName("");
                    setOldName("");
                    setOpenInputId("");
                }
            }
        };

        window.addEventListener("mousedown", handleClickOutside);

        return () => {
            window.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dispatch, newName, renameId]);

    useEffect(() => {
        if (renameId && renameInputRef.current) {
            renameInputRef.current.focus();
        }
    }, [renameId]);

    //pin chat
    const handlePinChat = (conversationId: string, pin) => {
        const body = {
            isPinned: pin,
            conversationId: conversationId,
        };

        dispatch(chatActions.updateConversation(body));
    };

    useEffect(() => {
        if (Boolean(deleteConversationSuccess)) {
            dispatch(chatActions.resetDeleteConversationSuccess());
            dispatch(chatActions.getAllConversations(30, updatedPage));
            nextThreadId ? setThreadId(nextThreadId) : handleCreateConversation();
            setDeleteId("");
            setConfirmDeleteChat(false);
            toast.custom((t) => (
                <CustomToast
                    t={t}
                    title={"Chat deleted"}
                    message={deleteConversationSuccess?.message}
                    type={"success"}
                />
            ));
        }
    }, [dispatch, deleteConversationSuccess, updatedPage, nextThreadId]);

    //delete chat
    const handleDeleteChat = (conversationId: string) => {
        dispatch(chatActions.deleteConversation(conversationId));
    };

    useEffect(() => {
        if (Boolean(updateConversationSuccess)) {
            dispatch(chatActions.resetUpdateConversationSuccess());
            dispatch(chatActions.getAllConversations(30, updatedPage));
            if (newName) {
                toast.custom((t) => (
                    <CustomToast t={t} message={"Chat renamed"} type={"success"} />
                ));
                // setNewName("");
                setOldName("");
            } else {
                toast.custom((t) => (
                    <CustomToast
                        t={t}
                        title={pinAction === "Pinned" ? "Chat pinned" : "Chat unpinned"}
                        message={
                            pinAction === "Pinned"
                                ? "Chat is now pinned for quick access."
                                : "Chat is now unpinned."
                        }
                        type={"success"}
                    />
                ));
                setDeleteId("");
            }
        }
    }, [dispatch, updateConversationSuccess, pinAction, updatedPage]);

    useEffect(() => {
        if (Boolean(createConversationSuccess)) {
            // setMessages([]);
            reset();
            setThreadId(createConversationSuccess?.conversation?.threadId);

            dispatch(chatActions.resetCreateConversationSuccess());
            dispatch(chatActions.getAllConversations(30, updatedPage));
        }
    }, [dispatch, createConversationSuccess, updatedPage]);

    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTo({
                top: chatRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [visibleMessages]);

    const sendMessage = async (content: string) => {
        if (!threadId) {
            await handleCreateConversation();
        }
        if (content.trim()) appendMessage(new TextMessage({ content, role: Role.User }));
        setMessage("");
    };

    useEffect(() => {
        if (selectedProducts?.length > 0) {
            handleCreateConversation();
        } else {
            if (threadId && threadId !== fetchChatThreadSuccess) {
                dispatch(chatActions.fetchChatThread(threadId));
                setCurrentThreadId("");
            } else if (!threadId) {
                dispatch(chatActions.resetFetchChatThreadSuccess());
                if (allConversations?.length > 0) {
                    setCurrentThreadId(allConversations[0]?.threadId);
                    setCurrentChat(
                        allConversations[0]?.lastMessage?.message ||
                            allConversations[0]?.title ||
                            "New sales target"
                    );
                } else {
                    handleCreateConversation();
                }
            }
        }
    }, [threadId, dispatch, allConversations, selectedProducts]);

    useEffect(() => {
        if (currentThreadId) {
            setThreadId(currentThreadId);
        }
    }, [currentThreadId]);

    useEffect(() => {
        if (Boolean(fetchChatThreadSuccess)) {
            setThreadId(fetchChatThreadSuccess);
        }
    }, [dispatch, fetchChatThreadSuccess]);

    const handleSelectChat = (id: string) => {
        if (threadId === id) {
            return;
        }
        if (visibleMessages?.length > 0) {
            // setMessages([]);
            reset();
        }
        setThreadId(id);
        setMessage("");
    };

    return (
        <div className="">
            <ModalContainer
                open={open}
                modalClassName={"py-10 px-10"}
                tailwindClassName="max-w-[1024px] !overflow-y-clip"
                showCloseIcon={false}
                closeModal={() => {
                    setOpen(false);
                    updateSearchParams({
                        updated: "true",
                    });
                }}
            >
                <div className="flex gap-2 relative justify-center ">
                    {/* {previousChatLayout && ( */}
                    <div
                        className={`bg-white rounded-[8px] relative transition-all duration-200 ease-in-out   ${
                            previousChatLayout ? "opacity-100 !w-[250px] " : "opacity-0 !w-0 !h-0 "
                        }`}
                    >
                        <div className=" w-full p-3 border-b border-slate-200">
                            <p className="text-base font-rocGroteskMedium text-g-75 ">
                                All Sales target
                            </p>
                        </div>
                        <div className="p-3">
                            {visibleMessages?.length > 0 ? (
                                <div
                                    className="bg-gradient-to-r from-[#62CC00] to-[#FFB902] rounded px-4 py-2 text-center cursor-pointer"
                                    onClick={() => {
                                        handleCreateConversation();
                                        setMessage("");
                                        updateSearchParams({
                                            updated: "true",
                                        });
                                    }}
                                >
                                    <p className="text-white text-[13px] ">New sales target</p>
                                </div>
                            ) : (
                                <SynthTooltip
                                    divId={"new-sales-target-tooltip"}
                                    position={"bottom"}
                                    icon={
                                        <div className="bg-gradient-to-r from-[#62CC00] to-[#FFB902] rounded px-4 py-2 text-center  opacity-70">
                                            <p className="text-white text-[13px] ">
                                                New sales target
                                            </p>
                                        </div>
                                    }
                                    content="You're already in a new chat. Finish or close this conversation before starting a new one."
                                    styling={{
                                        width: "232px",
                                        background: "#142837",
                                        borderRadius: "6px",
                                    }}
                                    contentClass="text-xs text-white"
                                />
                            )}

                            <div
                                className="max-h-[600px] h-[70vh] overflow-y-auto space-y-3 mt-6 text-sm font-rocGroteskMedium"
                                id="scrollContainer"
                            >
                                {previousChat &&
                                    Object?.entries(previousChat)?.length > 0 &&
                                    Object?.entries(previousChat)?.map(([key, value]) => {
                                        return (
                                            <div
                                                className="flex flex-col items-start justify-between"
                                                key={key}
                                            >
                                                <p className="text-sm text-g-75 p-2">{key}</p>
                                                <div className="w-full space-y-1 ">
                                                    {value?.map((msg, id) => {
                                                        const nextId = value[id + 1]?.threadId
                                                            ? value[id + 1]?.threadId
                                                            : value[id - 1]?.threadId
                                                            ? value[id - 1]?.threadId
                                                            : "";

                                                        return (
                                                            <div className="" key={id}>
                                                                {renameId === msg?._id ? (
                                                                    <div className="p-2 border-[#FFB902] border-[1.5px] rounded-md ">
                                                                        <input
                                                                            name="name"
                                                                            value={newName}
                                                                            onChange={(e) => {
                                                                                e.stopPropagation();
                                                                                setNewName(
                                                                                    e.target.value
                                                                                );
                                                                            }}
                                                                            disabled={
                                                                                renameId !==
                                                                                msg?._id
                                                                            }
                                                                            ref={renameInputRef}
                                                                            className="!w-full !outline-none  !text-g-75 bg-[#CCEBFD]"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        key={id}
                                                                        id="chat-item"
                                                                        className={`text-sm text-slate-500 hover:bg-slate-100 hover:text-g-75
                                                                          rounded p-2 cursor-pointer group flex items-center justify-between w-full relative ${
                                                                              threadId ===
                                                                              msg?.threadId
                                                                                  ? "bg-slate-100"
                                                                                  : ""
                                                                          } `}
                                                                        onClick={() => {
                                                                            updateSearchParams({
                                                                                updated: "true",
                                                                            });
                                                                            handleSelectChat(
                                                                                msg?.threadId
                                                                            );
                                                                            setCurrentChat(
                                                                                newName &&
                                                                                    openInputId ===
                                                                                        msg?._id &&
                                                                                    newName !== ""
                                                                                    ? newName
                                                                                    : msg
                                                                                          ?.lastMessage
                                                                                          ?.message ||
                                                                                          msg?.title ||
                                                                                          ""
                                                                            );
                                                                        }}
                                                                    >
                                                                        <p className="">
                                                                            {truncate(
                                                                                newName &&
                                                                                    openInputId ===
                                                                                        msg?._id &&
                                                                                    newName !== ""
                                                                                    ? newName
                                                                                    : msg
                                                                                          ?.lastMessage
                                                                                          ?.message ||
                                                                                          msg?.title ||
                                                                                          "New Chat",
                                                                                {
                                                                                    length: 23,
                                                                                }
                                                                            )}
                                                                        </p>

                                                                        <div
                                                                            className={`transition-all duration-300 relative z-[100] ${
                                                                                threadId ===
                                                                                msg?.threadId
                                                                                    ? "opacity-100"
                                                                                    : "opacity-0 group-hover:opacity-100"
                                                                            } `}
                                                                        >
                                                                            <Dropdown
                                                                                value={""}
                                                                                dropdown={
                                                                                    <i className="ri-more-2-fill text-base text-g-75 "></i>
                                                                                }
                                                                                dropdownItemsClasses="!border-t !border-t-slate-100 !py-2"
                                                                                dropdownClassName={`shadow-cardShadow-4 -left-[140px]  border border-slate-100 !w-[159px] rounded !max-h-fit`}
                                                                                dropdownContainerClasses={`shadow-cardShadow-4 -left-[140px]  border border-slate-100 !w-[159px] rounded !max-h-fit !py-0`}
                                                                                name={
                                                                                    "faq-dropdown"
                                                                                }
                                                                                dropdownOptions={[
                                                                                    {
                                                                                        label: msg?.isPinned
                                                                                            ? "Unpin chat"
                                                                                            : "Pin chat",
                                                                                        value: "pin",
                                                                                        icon: (
                                                                                            <i className="ri-pushpin-line text-base text-g-75 "></i>
                                                                                        ),
                                                                                        action: () => {
                                                                                            setPinAction(
                                                                                                msg?.isPinned
                                                                                                    ? "Unpinned"
                                                                                                    : "Pinned"
                                                                                            );

                                                                                            msg?.isPinned
                                                                                                ? handlePinChat(
                                                                                                      msg?._id,
                                                                                                      false
                                                                                                  )
                                                                                                : handlePinChat(
                                                                                                      msg?._id,
                                                                                                      true
                                                                                                  );
                                                                                        },
                                                                                    },
                                                                                    {
                                                                                        label: "Rename chat",
                                                                                        value: "rename",
                                                                                        action: (
                                                                                            event
                                                                                        ) => {
                                                                                            setRenameId(
                                                                                                msg?._id
                                                                                            );
                                                                                            setOpenInputId(
                                                                                                msg?._id
                                                                                            );
                                                                                            setOldName(
                                                                                                msg
                                                                                                    ?.lastMessage
                                                                                                    ?.message ||
                                                                                                    msg?.title ||
                                                                                                    "New chat"
                                                                                            );
                                                                                            setNewName(
                                                                                                msg
                                                                                                    ?.lastMessage
                                                                                                    ?.message ||
                                                                                                    msg?.title ||
                                                                                                    "New chat"
                                                                                            );
                                                                                        },
                                                                                        icon: (
                                                                                            <i className="ri-edit-line text-base text-g-75 "></i>
                                                                                        ),
                                                                                    },
                                                                                    {
                                                                                        label: "Delete chat",
                                                                                        value: "delete",
                                                                                        action: (
                                                                                            event
                                                                                        ) => {
                                                                                            setConfirmDeleteChat(
                                                                                                true
                                                                                            );
                                                                                            setDeleteId(
                                                                                                msg?._id
                                                                                            );
                                                                                            setNextThreadId(
                                                                                                nextId
                                                                                            );
                                                                                        },
                                                                                        icon: (
                                                                                            <i className="ri-delete-bin-line text-base text-[#E4281D] "></i>
                                                                                        ),
                                                                                        labelClassName:
                                                                                            "!text-[#E4281D] !text-sm",
                                                                                    },
                                                                                ]}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}

                                {loadingMoreConverstions && (
                                    <div className="flex items-center justify-center my-5">
                                        <Loader />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div
                        className={`bg-white shadow-card Shadow relative rounded-lg font-rocGroteskMedium h-auto`}
                        style={{
                            width: `calc(100% - ${previousChatLayout ? "250px" : "0px"})`,
                        }}
                    >
                        <div className="px-6 w-full flex py-3 border-b border-slate-200 text-center items-center justify-between ">
                            <div className="flex items-center gap-2 ">
                                <i
                                    className={` text-g-75 text-sm cursor-pointer transition-all duration-300 ${
                                        previousChatLayout
                                            ? "ri-layout-right-2-line"
                                            : "ri-layout-left-2-line"
                                    }`}
                                    onClick={() => {
                                        setPreviousChatLayout(!previousChatLayout);
                                    }}
                                ></i>
                                <p className="text-base font-rocGroteskMedium bg-gradient-to-r from-[#56B648] to-[#FFB902] bg-clip-text text-transparent">
                                    {truncate(currentChat, { length: 50 }) || "New sales target"}
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                {/* <Button
                                    btnText={"New chat"}
                                    type={"submit"}
                                    btnClassname="!text-md !text-white !w-fit !bg-gradient-to-r from-[#56B648] to-[#FFB902] !rounded-[8px] !py-1.5 !px-2  shadow-[0px_4px_6px_-2px_rgba(236,46,35,0.12),0px_12px_16px_0px_rgba(122,80,211,0.12),0px_4px_8px_0px_rgba(103,86,239,0.1)]"
                                    btnTextClassName="!text-md !text-white"
                                    icon={<i className="ri-add-line text-white"></i>}
                                    onClick={handleCreateConversation}
                                /> */}
                                <i
                                    className="ri-close-fill text-slate-500 text-lg cursor-pointer"
                                    onClick={() => {
                                        setOpen(false);
                                        updateSearchParams({
                                            updated: "true",
                                        });
                                    }}
                                ></i>
                            </div>
                        </div>

                        <div className="p-6 space-y-2">
                            <div
                                className="h-[400px] overflow-y-auto  overflow-x-clip space-y-2 flex flex-col justify-between"
                                ref={chatRef}
                            >
                                <div className="">
                                    <div className="">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-4 h-4 rounded-full flex items-center justify-center bg-g-75">
                                                <img
                                                    src="https://storage.googleapis.com/heysynth_files/synth_webapp/Group%201.svg"
                                                    alt="synth logo"
                                                    className="w-3 h-3"
                                                />
                                            </div>
                                            <p className="text-sm text-g-75">Synth</p>
                                        </div>
                                        <div
                                            className={`flex items-center gap-2 justify-start mb-2`}
                                        >
                                            <p
                                                className={`w-fit max-w-[80%] rounded-xl p-3.5 text-sm
                                             bg-[#D0F4ED59] text-g-75  `}
                                            >
                                                Hi there 👋 I'm Synth, your AI Operations Assistant.
                                                Tell me your sales goals, and I'll turn them into a
                                                forecast and target. What are we aiming for today?
                                            </p>
                                        </div>
                                    </div>

                                    {visibleMessages?.map((message, idx) => {
                                        // @ts-ignore
                                        const currentUser = visibleMessages[idx]?.role;
                                        // @ts-ignore
                                        const previousUser = visibleMessages[idx - 1]?.role;

                                        const messageContent =
                                            "isTextMessage" in message && message.isTextMessage()
                                                ? message.content
                                                : (message as any).content;
                                        const messageRole =
                                            "isTextMessage" in message && message.isTextMessage()
                                                ? message.role
                                                : (message as any).role;

                                        const content = "content" in message;

                                        const type = "type" in message && message.type;

                                        const executionMessage =
                                            "type" in message &&
                                            message.type === "AgentStateMessage" &&
                                            content &&
                                            (message.content as string);
                                        const tableMessageId =
                                            "id" in message &&
                                            message.type === "ResultMessage" &&
                                            message.id;

                                        const result =
                                            "type" in message && message.type === "ResultMessage";

                                        const isStartOfAssistantThread = previousUser === Role.User;

                                        const errorId =
                                            "id" in message &&
                                            message.type === "TextMessage" &&
                                            "content" in message &&
                                            (message.content as string)?.startsWith("An error") &&
                                            messageRole === Role.Assistant &&
                                            message.id;

                                        let resultMessage: any = null;

                                        if (
                                            message &&
                                            typeof message === "object" &&
                                            message.type === "ResultMessage" &&
                                            "result" in message &&
                                            typeof message.result === "string" &&
                                            "actionName" in message &&
                                            message?.actionName === "GenerateSalesTargetForecast"
                                        ) {
                                            try {
                                                resultMessage = JSON.parse(message.result);
                                            } catch (e) {}
                                        }

                                        if (resultMessage?.salesTarget?.length > 0) {
                                            return (
                                                <div className="mb-2">
                                                    <SalesTargetTable
                                                        salesTarget={resultMessage?.salesTarget}
                                                        summary={resultMessage?.summary}
                                                        threadId={threadId}
                                                        messageId={tableMessageId}
                                                    />
                                                </div>
                                            );
                                        }

                                        if (messageRole !== Role.User) {
                                            return (
                                                <div className="mb-2" key={idx}>
                                                    {isStartOfAssistantThread && (
                                                        <>
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <div className="w-4 h-4 rounded-full flex items-center justify-center bg-g-75">
                                                                    <img
                                                                        src="https://storage.googleapis.com/heysynth_files/synth_webapp/Group%201.svg"
                                                                        alt="synth logo"
                                                                        className="w-3 h-3"
                                                                    />
                                                                </div>
                                                                <p className="text-sm text-g-75">
                                                                    Synth
                                                                </p>
                                                            </div>
                                                        </>
                                                    )}

                                                    {/* {(isLoading || */}
                                                    {nodeName &&
                                                        nodeName !== "__end__" &&
                                                        idx === visibleMessages.length - 1 && (
                                                            <div className="flex items-center gap-2 justify-start">
                                                                <SalesTargetThinkingState
                                                                    isLoading={
                                                                        isLoading &&
                                                                        idx ===
                                                                            visibleMessages.length -
                                                                                1
                                                                    }
                                                                    // thought={executionMessage}
                                                                    // idx={idx}
                                                                />
                                                            </div>
                                                        )}

                                                    {type === "TextMessage" &&
                                                        messageContent?.length > 0 && (
                                                            <div
                                                                className={`flex items-center gap-2 justify-start`}
                                                            >
                                                                <div
                                                                    className={`w-fit max-w-[80%] rounded-xl p-3.5 text-sm
                                              ${
                                                  messageContent?.startsWith("An error")
                                                      ? "bg-[#FEEBE6] text-[#D43318]"
                                                      : "bg-[#D0F4ED59] text-g-75"
                                              } `}
                                                                >
                                                                    <ReactMarkdown>
                                                                        {messageContent || ""}
                                                                    </ReactMarkdown>
                                                                    {messageContent?.startsWith(
                                                                        "An error"
                                                                    ) && (
                                                                        <p
                                                                            className="text-[#2B71CC] text-sm cursor-pointer underline"
                                                                            onClick={() => {
                                                                                if (errorId) {
                                                                                    reloadMessages(
                                                                                        errorId
                                                                                    );
                                                                                }
                                                                            }}
                                                                        >
                                                                            {" "}
                                                                            Try again
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                </div>
                                            );
                                        }

                                        if (messageRole === Role.User) {
                                            const parts = messageContent.split(
                                                /(@SKU:[A-Z0-9]+-\([^)]+\))/g
                                            );
                                            return (
                                                <div className="mb-2" key={idx}>
                                                    <div
                                                        className={`flex items-center gap-2 justify-end`}
                                                    >
                                                        {/* <p>{messageContent}</p> */}
                                                        <p
                                                            className={`w-fit max-w-[80%] rounded-xl p-3.5 text-sm
                                            bg-slate-100 text-slate-500 `}
                                                        >
                                                            {parts?.map((part, idx) =>
                                                                part.match(
                                                                    /@SKU:([A-Z0-9]+)-\(([^)]+)\)/
                                                                ) ? (
                                                                    <span
                                                                        key={idx}
                                                                        className="!text-[#0054C2] !bg-[#E0EBF7] p-1 space-y-1"
                                                                    >
                                                                        {part}
                                                                    </span>
                                                                ) : (
                                                                    part
                                                                )
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    })}
                                </div>
                                {/* examples */}
                                {visibleMessages?.length === 0 && (
                                    <div className="">
                                        <p className="text-sm text-slate-500">
                                            Try these examples:
                                        </p>
                                        {/* <MyComponent /> */}
                                        <div className="mt-2 ">
                                            <div className="flex items-center min-w-0 space-x-2 overflow-x-auto w-[100%] cursor-pointer">
                                                {[
                                                    "I have a sales target of 6000 units over the next...",
                                                    "Generate a forecast for 8000 units over the next 4 months... ",
                                                    "Can you recalculate if I increase...",
                                                ]?.map((item, idx) => {
                                                    return (
                                                        <span
                                                            className="text-sm text-slate-500 bg-slate-100 rounded-lg p-2 w-fit flex-shrink-0"
                                                            key={idx}
                                                            onClick={() => {
                                                                setMessage(item?.split("...")[0]);
                                                            }}
                                                        >
                                                            {item}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="">
                                {/* text input */}
                                <div
                                    className="border border-slate-200 rounded-lg px mt-2
                            shadow-[0px_4px_12px_-2px_rgba(212,212,212,0.03),0px_10px_15px_-4px_rgba(212,212,212,0.01),0px_8px_6px_0px_rgba(212,212,212,0.04)]"
                                >
                                    {/* {(uploadingDoc || filesAttached?.length > 0) && ( */}
                                    <div className="flex flex-wrap items-center gap-2 p-4 !pb-0 overflow-y-auto max-h-[80px]">
                                        {loadingAttachName && !uploadingDoc && (
                                            <div className="mb-2 items-center px-3 py-2  rounded-[6px] space-x-3 border border-slate-100  width-[250px] justify-between  opacity-50 inline-flex">
                                                <div className="flex items-center gap-2">
                                                    <div className="bg-n-20 rounded-[6px] w-[25px] h-[25px] flex items-center justify-center">
                                                        <Loader size={4} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-rocGroteskMedium text[#64748B]">
                                                            {truncate(loadingAttachName, {
                                                                length: 30,
                                                            })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {filesAttached?.length > 0 &&
                                            filesAttached?.map((doc, idx) => (
                                                <div className="inline-flex">
                                                    <div className="flex space-x-1 w-full items-center">
                                                        <div className="flex mb-2 items-center px-3 py-2  rounded-[6px] space-x-3 border border-slate-100 flex-shrink-0 width-[250px] justify-between bg-[#F1F5F9]">
                                                            <div className="flex items-center gap-2">
                                                                {displayFileIcon(
                                                                    getFileTypeFromUrl(
                                                                        doc?.fileName
                                                                    ),
                                                                    true,
                                                                    "w-[25px] h-[25px]"
                                                                )}
                                                                <div>
                                                                    <p className="text-sm font-rocGroteskMedium text-gm-50">
                                                                        {truncate(doc?.fileName, {
                                                                            length: 30,
                                                                        })}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <i
                                                                className="ri-close-fill text-gm-50 cursor-pointer"
                                                                onClick={() =>
                                                                    deleteAttachment(idx)
                                                                }
                                                            ></i>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                        <div
                                            className="flex items-center  w-fit gap-4 cursor-pointer "
                                            onClick={() =>
                                                document.getElementById("fileInput")?.click()
                                            }
                                        >
                                            <div className="flex items-center gap-2 border border-[#E2E8F0] py-1.5 px-4 rounded-md cursor-pointer  flex-shrink-0 -mt-[5px]">
                                                <p
                                                    className={` rounded-[6px]  text-lg flex items-center justify-center text-[#6562E5] ${
                                                        filesAttached?.length === 0
                                                            ? "h-6 w-6 bg-slate-100 "
                                                            : ""
                                                    }`}
                                                >
                                                    +
                                                </p>
                                                {filesAttached?.length === 0 && (
                                                    <p className="text-xs text-g-75">
                                                        Add file for context
                                                    </p>
                                                )}
                                            </div>
                                            <input
                                                type="file"
                                                id="fileInput"
                                                className={`absolute z-[-1] opacity-0`}
                                                // accept=".pdf"
                                                multiple
                                                onChange={(evt) => {
                                                    if (
                                                        !isFileSizeLessThanOrEqualTo(
                                                            evt.target.files[0],
                                                            25
                                                        )
                                                    ) {
                                                        toast.custom((t) => (
                                                            <CustomToast
                                                                t={t}
                                                                message={
                                                                    "File to large, file size cannot be more 25mb"
                                                                }
                                                                type={"error"}
                                                            />
                                                        ));
                                                        return;
                                                    } else {
                                                        setLoadingAttachName(
                                                            evt.target.files[0].name
                                                        );
                                                        handleUpload(
                                                            evt.target.files[0].name,
                                                            evt.target.files[0]
                                                        );
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="relative" id="textAreaInput">
                                        <CustomEditor
                                            message={message}
                                            setMessage={setMessage}
                                            setSelectedIds={setSelectedIds}
                                            selectedIds={selectedIds}
                                            currentSelectedIds={currentSelectedIds}
                                            setCurrentSelectedIds={setCurrentSelectedIds}
                                            sendMessage={sendMessage}
                                            hasPreviousMessage={visibleMessages?.length > 0}
                                            threadId={threadId}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ModalContainer>
            <ModalContainer
                open={confirmDeleteChat}
                modalClassName={"py-10 px-10"}
                tailwindClassName="md:w-[600px] w-[90%] "
                showCloseIcon={false}
                closeModal={() => {
                    setConfirmDeleteChat(false);
                }}
            >
                <div className="bg-white py-7 px-6  shadow-cardShadow  rounded-[8px]">
                    <div className="mb-5 mt-4">
                        <h1 className="text-lg mb-2  font-rocGroteskMedium">Delete this chat?</h1>
                        <p className="text-slate-500 font-rocGroteskMedium text-base">
                            This action will permanently delete the entire conversation. You won't
                            be able to recover it later.
                        </p>
                    </div>
                    <div className="flex justify-end items-center ">
                        <div className="flex items-center !whitespace-nowrap space-x-2  ">
                            <Button
                                btnText={"No, cancel"}
                                type={"button"}
                                btnClassname={"!bg-n-20 !w-fit !text-gm-50"}
                                onClick={() => setConfirmDeleteChat(false)}
                            />
                            <Button
                                btnClassname={"!py-3 !w-fit !bg-[#E4281D] !text-white"}
                                onClick={() => {
                                    handleDeleteChat(deleteId);
                                }}
                                btnText={"Yes, delete chat"}
                                type={"button"}
                                isLoading={deletingConversation}
                            />
                        </div>
                    </div>
                </div>
            </ModalContainer>
        </div>
    );
};

export default SalesTargetAgent;
